"use client";

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ListIcon, PlusIcon, PencilIcon, Trash2Icon } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useAakRulesQuery, invalidateAakRulesQuery } from "@/lib/queries";
import { deleteAakRule } from "@/app/lib/actions/aak-rules";
import type { AakRule } from "@/prisma/generated/client";

import { FlagBadge } from "./aak-flag-badge";
import { AakRuleForm } from "./aak-rule-form";
import { AakLegend } from "./aak-legend";

function AdministrationAak() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useAakRulesQuery({
    sort: { saleNature: "asc" },
  });

  const [formOpen, setFormOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AakRule | null>(null);
  const [deletingRule, setDeletingRule] = useState<AakRule | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  const refresh = () => invalidateAakRulesQuery(queryClient);

  const openCreate = () => {
    setEditingRule(null);
    setFormOpen(true);
  };

  const openEdit = (rule: AakRule) => {
    setEditingRule(rule);
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingRule) return;
    setDeleteLoading(true);
    await deleteAakRule({ id: deletingRule.id });
    setDeleteLoading(false);
    setDeletingRule(null);
    refresh();
  };

  const rules = data?.aakRules ?? [];
  const total = data?.total ?? 0;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ListIcon className="text-primary size-5" />
                Référentiel des natures de vente{" "}
                {!isLoading && (
                  <span className="text-muted-foreground text-sm font-normal">
                    ({total} entrées)
                  </span>
                )}
              </CardTitle>
              <p className="text-muted-foreground mt-1 text-sm">
                Chaque nature de vente détermine les règles de comptabilisation
                AAK, livraison, réseau et garantie
              </p>
            </div>
            <Button size="sm" onClick={openCreate}>
              <PlusIcon className="mr-1.5 size-4" />
              Nouvelle règle
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {isError && (
            <p className="px-6 py-4 text-sm text-red-600">
              Erreur lors du chargement des règles AAK.
            </p>
          )}

          {isLoading ? (
            <div className="text-muted-foreground flex items-center justify-center py-12 text-sm">
              Chargement…
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-36">Nature de vente</TableHead>
                  <TableHead>Libellé</TableHead>
                  <TableHead className="w-32 text-center">Compta AAK</TableHead>
                  <TableHead className="w-40 text-center">
                    Nature de livraison
                  </TableHead>
                  <TableHead className="w-32 text-center">
                    Flag réseau
                  </TableHead>
                  <TableHead className="w-36 text-center">
                    Départ garantie
                  </TableHead>
                  <TableHead className="w-20" />
                </TableRow>
              </TableHeader>

              <TableBody>
                {rules.map((rule) => (
                  <TableRow
                    key={rule.id}
                    onMouseEnter={() => setHoveredId(rule.id)}
                    onMouseLeave={() => setHoveredId(null)}
                  >
                    <TableCell>
                      <span
                        style={{ backgroundColor: "rgb(15 49 143)" }}
                        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold text-white"
                      >
                        {rule.saleNature}
                      </span>
                    </TableCell>

                    <TableCell className="text-sm font-medium">
                      {rule.label}
                    </TableCell>

                    <TableCell className="text-center">
                      <FlagBadge value={rule.accountingFlag} />
                    </TableCell>

                    <TableCell className="text-center">
                      <FlagBadge value={rule.deliveryNature} />
                    </TableCell>

                    <TableCell className="text-center">
                      <FlagBadge value={rule.networkFlag} />
                    </TableCell>

                    <TableCell className="text-center">
                      <FlagBadge value={rule.warrantyStart} />
                    </TableCell>

                    <TableCell>
                      <div
                        style={{
                          opacity: hoveredId === rule.id ? 1 : 0,
                          transition: "opacity 0.15s",
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-primary size-7"
                          onClick={() => openEdit(rule)}
                        >
                          <PencilIcon className="size-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-destructive size-7"
                          onClick={() => setDeletingRule(rule)}
                        >
                          <Trash2Icon className="size-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {!isLoading && rules.length > 0 && (
            <div className="text-muted-foreground border-t px-6 py-3 text-xs">
              {total} règles actives · Versionnement actif
            </div>
          )}
        </CardContent>
        <AakLegend />
      </Card>

      <AakRuleForm
        key={editingRule?.id ?? "new"}
        rule={editingRule}
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={refresh}
      />

      <AlertDialog
        open={!!deletingRule}
        onOpenChange={(o) => !o && setDeletingRule(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la nature de vente ?</AlertDialogTitle>
            <AlertDialogDescription>
              La règle{" "}
              <strong>
                {deletingRule?.saleNature} — {deletingRule?.label}
              </strong>{" "}
              sera désactivée (soft delete). Cette action peut être annulée via
              le versionning.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteLoading ? "Suppression…" : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export { AdministrationAak };
